import { useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa6";

const SMOOTH = "cubic-bezier(0.4, 0, 0.2, 1)";

const Checkbox = ({ checked, accent }) => (
  <Box
    as="span"
    width="16px"
    height="16px"
    borderRadius="5px"
    bg={
      checked
        ? `linear-gradient(135deg, ${accent}, ${accent}cc)`
        : "transparent"
    }
    border="1px solid"
    borderColor={checked ? accent : "rgba(15,22,38,0.18)"}
    display="inline-grid"
    placeItems="center"
    flexShrink={0}
  >
    {checked && (
      <Box as="svg" width="10px" height="10px" viewBox="0 0 12 12">
        <path
          d="M2 6.5 L5 9 L10 3.5"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    )}
  </Box>
);

const MultiSelect = ({
  label,
  value,
  onChange,
  options,
  icon,
  accent = "#04aec1",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filled = value.length > 0;
  const isFloating = filled || open;
  const display = filled ? value.join(", ") : "";
  const iconLeft = icon ? "38px" : "14px";

  return (
    <Box ref={ref} position="relative">
      <Box
        as="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        width="100%"
        textAlign="left"
        bg={open ? "#ffffff" : "rgba(255,255,255,0.85)"}
        border="1px solid"
        borderColor={open ? accent : "rgba(15,22,38,0.10)"}
        borderRadius="12px"
        cursor="pointer"
        position="relative"
        minH="56px"
        boxShadow={
          open
            ? `0 0 0 4px ${accent}22, 0 8px 22px -10px ${accent}55`
            : "none"
        }
        transition={`all .2s ${SMOOTH}`}
        padding={`${isFloating ? "22px 14px 8px" : "20px 14px"}`}
        pl={iconLeft}
        pr="38px"
        fontSize="14px"
        fontWeight={500}
        color={filled ? "#0a0e1a" : "rgba(15,22,38,0.5)"}
      >
        {icon && (
          <Flex
            position="absolute"
            left="14px"
            top="50%"
            transform="translateY(-50%)"
            color={open ? accent : "rgba(15,22,38,0.55)"}
            fontSize="14px"
            align="center"
          >
            {icon}
          </Flex>
        )}

        <Box
          as="span"
          position="absolute"
          left={iconLeft}
          top={isFloating ? "6px" : "50%"}
          transform={isFloating ? "translateY(0)" : "translateY(-50%)"}
          fontSize={isFloating ? "10px" : "13px"}
          fontWeight={isFloating ? 600 : 500}
          letterSpacing={isFloating ? "0.08em" : 0}
          textTransform={isFloating ? "uppercase" : "none"}
          color={open ? accent : "rgba(15,22,38,0.55)"}
          transition={`all .2s ${SMOOTH}`}
          pointerEvents="none"
        >
          {label}
        </Box>

        <Box
          as="span"
          display="block"
          mt={filled ? "2px" : 0}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {display || " "}
        </Box>

        <Box
          as="span"
          position="absolute"
          right="14px"
          top="50%"
          transform={`translateY(-50%) rotate(${open ? 180 : 0}deg)`}
          color="rgba(15,22,38,0.5)"
          fontSize="11px"
          transition={`transform .2s ${SMOOTH}`}
          display="grid"
          placeItems="center"
        >
          <FaChevronDown />
        </Box>
      </Box>

      {open && (
        <Box
          position="absolute"
          left={0}
          right={0}
          top="calc(100% + 6px)"
          bg="white"
          border="1px solid rgba(15,22,38,0.10)"
          borderRadius="12px"
          boxShadow="0 18px 40px -18px rgba(15,22,38,0.18)"
          padding="6px"
          maxH="260px"
          overflowY="auto"
          zIndex={50}
        >
          {options.map((opt) => {
            const checked = value.includes(opt);
            return (
              <Box
                as="button"
                type="button"
                key={opt}
                onClick={() =>
                  onChange(
                    checked ? value.filter((v) => v !== opt) : [...value, opt],
                  )
                }
                display="flex"
                alignItems="center"
                gap="10px"
                width="100%"
                padding="8px 10px"
                borderRadius="8px"
                bg={checked ? `${accent}10` : "transparent"}
                border="none"
                cursor="pointer"
                textAlign="left"
                fontSize="13px"
                fontWeight={500}
                color="#0a0e1a"
                transition={`background .15s ${SMOOTH}`}
                _hover={{
                  bg: checked ? `${accent}10` : "rgba(15,22,38,0.04)",
                }}
              >
                <Checkbox checked={checked} accent={accent} />
                <Text as="span">{opt}</Text>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MultiSelect;
